import React from 'react';
import {
    Box,
    Flex,
    HStack,
    Icon,
    IconButton,
    Text,
    Tooltip,
    VStack,
    useColorMode,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    AiOutlineExpandAlt,
    AiOutlineClose,
    AiOutlineForm,
} from 'react-icons/ai';
import { useModalMinimizer } from '../../contexts/ModalMinimizerContext';

const MinimizedModalsWidget = () => {
    const { minimizedModals, restoreModal, removeMinimizedModal } = useModalMinimizer();
    const { colorMode } = useColorMode();

    if (!minimizedModals || minimizedModals.length === 0) return null;

    return (
        <Box
            position="fixed"
            bottom="20px"
            right="20px"
            zIndex={99999}
            pointerEvents="none"
        >
            <VStack spacing={2.5} align="flex-end">
                <AnimatePresence>
                    {minimizedModals.map((m) => {
                        const IconComponent = m.icon || AiOutlineForm;
                        return (
                            <Box
                                key={m.id}
                                as={motion.div}
                                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                                transition={{ duration: 0.25, ease: 'easeOut' }}
                                pointerEvents="auto"
                                bg={colorMode === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)'}
                                backdropFilter="blur(12px)"
                                border="1px solid"
                                borderColor={colorMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}
                                borderRadius="xl"
                                px={3.5}
                                py={2.5}
                                boxShadow="0 10px 30px rgba(0, 0, 0, 0.45), 0 0 1px rgba(1, 233, 117, 0.3)"
                                cursor="pointer"
                                onClick={() => restoreModal(m.id)}
                                _hover={{
                                    borderColor: 'secondary',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.55), 0 0 8px rgba(1, 233, 117, 0.5)',
                                }}
                                transitionProperty="all"
                                transitionDuration="0.2s"
                                maxW="320px"
                                minW="220px"
                            >
                                <HStack spacing={3} align="center">
                                    {/* Indicador de cor e ícone */}
                                    <Flex
                                        w="32px"
                                        h="32px"
                                        borderRadius="lg"
                                        bg="rgba(1, 233, 117, 0.15)"
                                        align="center"
                                        justify="center"
                                        flexShrink={0}
                                    >
                                        <Icon as={IconComponent} color="secondary" boxSize="16px" />
                                    </Flex>

                                    {/* Título e subtítulo */}
                                    <Box flex={1} minW={0}>
                                        <Text
                                            fontSize="xs"
                                            fontWeight="800"
                                            color="textPrimary"
                                            lineHeight="1.2"
                                            isTruncated
                                        >
                                            {m.title || 'Janela Minimizada'}
                                        </Text>
                                        {m.subtitle && (
                                            <Text
                                                fontSize="10px"
                                                color="textSecondary"
                                                fontWeight="600"
                                                isTruncated
                                                mt={0.5}
                                            >
                                                {m.subtitle}
                                            </Text>
                                        )}
                                    </Box>

                                    {/* Ações: Expandir e Fechar */}
                                    <HStack spacing={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                                        <Tooltip label="Expandir" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Expandir Modal"
                                                icon={<Icon as={AiOutlineExpandAlt} boxSize="15px" />}
                                                size="xs"
                                                variant="ghost"
                                                color="secondary"
                                                _hover={{ bg: 'whiteAlpha.200' }}
                                                borderRadius="md"
                                                onClick={() => restoreModal(m.id)}
                                            />
                                        </Tooltip>
                                        <Tooltip label="Fechar Definitivamente" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Fechar Modal"
                                                icon={<Icon as={AiOutlineClose} boxSize="13px" />}
                                                size="xs"
                                                variant="ghost"
                                                color="textSecondary"
                                                _hover={{ bg: 'red.500', color: 'white' }}
                                                borderRadius="md"
                                                onClick={() => removeMinimizedModal(m.id)}
                                            />
                                        </Tooltip>
                                    </HStack>
                                </HStack>
                            </Box>
                        );
                    })}
                </AnimatePresence>
            </VStack>
        </Box>
    );
};

export default MinimizedModalsWidget;
